import {
  addDays,
  addHours,
  differenceInHours,
  format,
  isAfter,
  subDays,
  subHours,
} from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { Stack, useFocusEffect, useRouter } from 'expo-router';
import { DateTime } from 'luxon';
import React from 'react';
import { Platform, Pressable, ScrollView, View } from 'react-native';
import {
  createStyleSheet,
  UnistylesRuntime,
  useStyles,
} from 'react-native-unistyles';

import type { Extra } from '@/api';
import type { ExtensionOption } from '@/api/reservations/use-available-extensions';
import { useAvailableExtensions } from '@/api/reservations/use-available-extensions';
import type { CreateExtensionIntent } from '@/api/reservations/use-create-extension-intent';
import { useCreateExtensionIntent } from '@/api/reservations/use-create-extension-intent';
import { useExtendReservation } from '@/api/reservations/use-extend-reservation';
import { useReservation } from '@/api/reservations/use-reservation';
import ContactSupport from '@/components/contact-support';
import { CardItem } from '@/components/trip-extension/card-item';
import { formatNumber, openAddressInMaps } from '@/core';
import { Button, Card, showErrorMessage, Text } from '@/ui';
import { Calendar } from '@/ui/calendar';
import { Chip, ChipType } from '@/ui/chip';
import { Icon } from '@/ui/icon';
import { ArrowRight } from '@/ui/icons';
import { AppStore } from '@/ui/icons/app-store';
import { Calendar as CalendarIcon } from '@/ui/icons/calendar';
import { GooglePlay } from '@/ui/icons/google-play';
import { Loader } from '@/ui/loader';
import { Tooltip } from '@/ui/tooltip';

import BackButton from '../back-button';
import { NavigationButton, NavigationButtonTypes } from '../navigation-button';
import type { TripStep } from '../trip-timeline';
import TripTimeline, { StepType } from '../trip-timeline';
import ExtensionPayment from './extension-payment';

export default function TripExtension({ id }: { id: number }) {
  const { styles, theme } = useStyles(stylesheet);
  const router = useRouter();
  const [activeQuickExtensionOption, setActiveQuickExtensionOption] =
    React.useState<ExtensionOption | null>();
  const [showCalendar, setShowCalendar] = React.useState(false);
  const [endDatetime, setEndDatetime] = React.useState<Date | null>(null);
  const [extensionIntent, setExtensionIntent] =
    React.useState<CreateExtensionIntent | null>();
  const [isHandlingPaymentPending, setIsHandlingPaymentPending] =
    React.useState(false);
  const [isHandlingPaymentSuccess, setIsHandlingPaymentSuccess] =
    React.useState(false);
  const [isHandlingPaymentError, setIsHandlingPaymentError] =
    React.useState(false);
  const [extendReservationSuccess, setExtendReservationSuccess] =
    React.useState(false);

  const {
    data: resData,
    isFetching: isFetchingReservation,
    refetch,
  } = useReservation({
    //@ts-ignore
    variables: { id: id },
    enabled: false,
  });

  const {
    mutateAsync: createExtensionIntent,
    isPending: isCreatingExtensionIntent,
  } = useCreateExtensionIntent();

  const { mutateAsync: extendReservation, isPending: isExtendingReservation } =
    useExtendReservation();

  const {
    data: availableExtensionsData,
    isLoading: isGettingAvailableExtensions,
  } = useAvailableExtensions({
    retry: false,
    variables: { id: id },
  });

  useFocusEffect(
    React.useCallback(() => {
      refetch();
    }, [refetch])
  );

  React.useEffect(() => {
    if (resData?.endTime) {
      setEndDatetime(addHours(new Date(resData.endTime), 1));
    }
  }, [resData]);

  const maxDate = React.useMemo(() => {
    if (!availableExtensionsData || !resData) return undefined;

    const nextTrip = availableExtensionsData.nextTrip;

    if (!nextTrip) return undefined;

    const cutoffStartDatetime = subHours(
      toZonedTime(nextTrip.startTime, resData.pickupLocation.timezone),
      availableExtensionsData.hoursBetweenTrips
    );

    return cutoffStartDatetime;
  }, [resData, availableExtensionsData]);

  const extensionUnavailable = React.useMemo(() => {
    if (!availableExtensionsData) return true;

    return (
      availableExtensionsData.quickExtensionOptions.find(
        (eo) => eo.available
      ) === undefined
    );
  }, [availableExtensionsData]);

  const timelineSteps = React.useMemo(() => {
    if (!resData) return [];

    const renderDepositStep = (): TripStep => {
      const lessThan24Hours =
        differenceInHours(new Date(resData.startTime), new Date()) < 24;

      let type = '';
      let body = '';
      if (resData.status === 'verified' && lessThan24Hours) {
        type = StepType.NEXT;
        body = 'Now';
      } else if (['pending', 'open', 'verified'].includes(resData.status)) {
        type = StepType.UPCOMING;
        body = format(
          subDays(
            toZonedTime(resData.startTime, resData.pickupLocation.timezone),
            1
          ),
          'MMM dd - hh:mm a'
        );
      } else {
        type = StepType.COMPLETED;
      }

      return {
        type: type as StepType,
        title: `Authorize your $${resData.holdAmount / 100} security deposit`,
        body: body,
      };
    };

    const renderCheckIn = (): TripStep => {
      let type = '';
      let body = '';

      if (resData.isCheckinComplete) {
        type = StepType.COMPLETED;
      } else if (
        ['confirmed', 'rental'].includes(resData.status) &&
        !resData.isCheckinComplete
      ) {
        type = StepType.NEXT;
        body = format(
          subHours(
            toZonedTime(resData.startTime, resData.pickupLocation.timezone),
            1
          ),
          'MMM dd - hh:mm a'
        );
      } else {
        type = StepType.UPCOMING;
        body = format(
          subHours(
            toZonedTime(resData.startTime, resData.pickupLocation.timezone),
            1
          ),
          'MMM dd - hh:mm a'
        );
      }

      return {
        type: type as StepType,
        title: 'Check-In',
        body: body,
      };
    };

    const renderPickup = (): TripStep => {
      let type = '';
      let body = '';

      const now = new Date();
      const startTimeDate = toZonedTime(
        resData.startTime,
        resData.pickupLocation.timezone
      );

      if (resData.isCheckinComplete && now > new Date(resData.startTime)) {
        type = StepType.COMPLETED;
      } else if (resData.isCheckinComplete) {
        type = StepType.NEXT;
        body = format(startTimeDate, 'MMM dd - hh:mm a');
      } else {
        type = StepType.UPCOMING;
        body = format(startTimeDate, 'MMM dd - hh:mm a');
      }

      return {
        type: type as StepType,
        title: resData.customPickupLocation ? 'Delivery' : 'Pickup',
        body: body,
        footer: (
          <Pressable
            onPress={() => {
              openAddressInMaps(
                resData.customPickupLocation || resData.pickupLocation.address
              );
            }}
          >
            <Text
              type="bodyRegular"
              style={{
                color: theme.colors.text.secondary,
                textDecorationLine: 'underline',
              }}
            >
              {resData.customPickupLocation || resData.pickupLocation.address}
            </Text>
          </Pressable>
        ),
      };
    };

    const renderDropoff = (): TripStep => {
      let type = '';
      let body = '';

      const now = new Date();
      const endDateTime = toZonedTime(
        resData.endTime,
        resData.returnLocation.timezone
      );

      if (['cleaning', 'billable', 'completed'].includes(resData.status)) {
        type = StepType.COMPLETED;
      } else if (
        resData.status === 'rental' &&
        now >= new Date(resData.endTime)
      ) {
        type = StepType.NEXT;
        body = 'Now';
      } else if (
        resData.isCheckinComplete &&
        now > new Date(resData.startTime) &&
        now < new Date(resData.endTime)
      ) {
        type = StepType.NEXT;
        body = format(endDateTime, 'MMM dd - hh:mm a');
      } else {
        type = StepType.UPCOMING;
        body = format(endDateTime, 'MMM dd - hh:mm a');
      }

      return {
        type: type as StepType,
        title: 'Dropoff',
        body: body,
        footer: (
          <Pressable
            onPress={() => {
              openAddressInMaps(
                resData.customReturnLocation || resData.returnLocation.address
              );
            }}
          >
            <Text
              type="bodyRegular"
              style={{
                color: theme.colors.text.secondary,
                textDecorationLine: 'underline',
              }}
            >
              {resData.customReturnLocation || resData.returnLocation.address}
            </Text>
          </Pressable>
        ),
      };
    };

    const renderFinalReceipt = (): TripStep => {
      let type = '';
      let body = '';

      const endDateTime = toZonedTime(
        resData.endTime,
        resData.returnLocation.timezone
      );

      if (['cleaning', 'billable'].includes(resData.status)) {
        type = StepType.NEXT;
        body = format(addDays(endDateTime, 2), 'MMM dd');
      } else if (resData.status === 'completed') {
        type = StepType.COMPLETED;
      } else {
        type = StepType.UPCOMING;
        body = format(addDays(endDateTime, 2), 'MMM dd');
      }

      return {
        type: type as StepType,
        title: 'Get your final receipt',
        body: body,
      };
    };

    const steps: TripStep[] = [
      {
        type: StepType.COMPLETED,
        title: 'Book Your Trip',
        body: '',
      },
      {
        type: ['pending', 'open'].includes(resData.status)
          ? StepType.NEXT
          : StepType.COMPLETED,
        title:
          "Confirm your insurance, driver's license and payment information.",
        body: ['pending', 'open'].includes(resData.status) ? 'Now' : '',
      },
      ...(resData.holdAmount > 0 ? [renderDepositStep()] : []),
      renderCheckIn(),
      renderPickup(),
      renderDropoff(),
      renderFinalReceipt(),
    ];

    return steps;
  }, [resData]);

  const handleCreateExtensionIntent = async (endTime: string) => {
    try {
      const res = await createExtensionIntent({
        id: resData!.id,
        endTime: endTime,
      });

      setExtensionIntent(res);
    } catch (error) {}
  };

  const handlePaymentSuccessful = async () => {
    try {
      await extendReservation({
        id: resData!.id,
        endTime: format(
          toZonedTime(
            extensionIntent!.data.endTime,
            resData!.pickupLocation.timezone
          ),
          'yyyy-MM-dd HH:mm:ss'
        ),
      });

      setExtendReservationSuccess(true);
      refetch();
    } catch (e: any) {
      showErrorMessage(e.message);
    }
  };

  const handleFreeExtension = async () => {
    try {
      await extendReservation({
        id: resData!.id,
        endTime: format(
          toZonedTime(
            extensionIntent!.data.endTime,
            resData!.pickupLocation.timezone
          ),
          'yyyy-MM-dd HH:mm:ss'
        ),
      });

      setExtendReservationSuccess(true);
      refetch();
    } catch (e: any) {
      showErrorMessage(e.message);
    }
  };

  if (isFetchingReservation || !resData) {
    return (
      <View
        style={{
          width: '100%',
          height: '100%',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Loader style={{ width: 150, height: 150 }} />
      </View>
    );
  }

  if (extendReservationSuccess) {
    return (
      <View style={styles.container}>
        {Platform.OS === 'web' &&
        ['md', 'lg'].includes(UnistylesRuntime.breakpoint) ? (
          <Stack.Screen
            options={{
              headerShown: false,
            }}
          />
        ) : (
          <Stack.Screen
            options={{
              title: 'Rental Extension',
              headerTitle: 'Rental Extension',
              headerLeft: () => <BackButton />,
              headerShown: true,
              headerStyle: {
                backgroundColor: theme.colors.background_1,
              },
              headerTintColor: theme.colors.text.primary,
              headerShadowVisible: false,
            }}
          />
        )}

        <View style={styles.scrollViewContainer}>
          <ScrollView
            showsVerticalScrollIndicator={false}
            contentContainerStyle={{ alignItems: 'center', padding: 16 }}
            style={{ width: '100%' }}
          >
            <View style={styles.scrollViewChild}>
              {['md', 'lg'].includes(UnistylesRuntime.breakpoint) ? (
                <View style={{ gap: 16 }}>
                  <NavigationButton
                    type={NavigationButtonTypes.back_with_text}
                    onPress={() => {
                      router.back();
                    }}
                  />

                  <Text type="largeTitle">Rental Extension</Text>
                </View>
              ) : null}

              <View style={{ gap: 16 }}>
                <Text type="title1">
                  Your Trip Has Been Successfully Extended!
                </Text>

                <Text type="bodyRegular">Here's your updated Eon timeline</Text>
              </View>

              <Card>
                <View
                  style={{
                    borderBottomColor: theme.colors.card.default.borderColor,
                    borderBottomWidth: 1,
                  }}
                >
                  <View style={{ padding: 16 }}>
                    <Text tx="checkoutComplete.rentalTimeline" type="title2" />
                  </View>
                </View>

                <View style={{ padding: 16 }}>
                  <TripTimeline steps={timelineSteps} />
                </View>
              </Card>

              {Platform.OS === 'web' ? (
                <Card>
                  <View style={{ padding: 16 }}>
                    <View style={{ gap: 8 }}>
                      <Text
                        tx="checkoutComplete.manageBookingsFromApp"
                        type="title2"
                      />

                      <Text
                        tx="checkoutComplete.manageBookingsFromAppBody"
                        type="bodyRegular"
                        style={{ color: theme.colors.text.secondary }}
                      />

                      <View
                        style={{
                          flexDirection: 'row',
                          justifyContent: 'center',
                        }}
                      >
                        <Pressable
                          onPress={async () => {
                            window.open(
                              'https://apps.apple.com/us/app/eon-instant-ev-rentals/id6448188911',
                              '_blank'
                            );
                          }}
                        >
                          <Icon
                            type="naked"
                            icon={<AppStore />}
                            width={160}
                            height={90}
                          />
                        </Pressable>

                        <Pressable
                          onPress={() => {
                            window.open(
                              'https://play.google.com/store/apps/details?id=com.eon.mobile',
                              '_blank'
                            );
                          }}
                        >
                          <Icon
                            type="naked"
                            icon={<GooglePlay />}
                            width={160}
                            height={90}
                          />
                        </Pressable>
                      </View>
                    </View>
                  </View>
                </Card>
              ) : null}
            </View>
          </ScrollView>
        </View>

        <View style={styles.overviewContainer}>
          <View
            style={{
              alignItems: 'center',
              paddingTop: 16,
            }}
          >
            <Button
              tx="checkoutComplete.manageYourBookings"
              type="primary"
              onPress={() => {
                if (Platform.OS === 'android' && router.canDismiss()) {
                  router.dismissAll();
                }
                router.navigate('/trips');
              }}
              style={{
                width: '80%',
              }}
            />
          </View>
        </View>
      </View>
    );
  }

  return (
    <ScrollView
      contentContainerStyle={{ flexGrow: 1, gap: 32, alignItems: 'center' }}
      style={{ flex: 1 }}
      showsVerticalScrollIndicator={false}
    >
      {Platform.OS === 'web' &&
      ['md', 'lg'].includes(UnistylesRuntime.breakpoint) ? (
        <Stack.Screen
          options={{
            headerShown: false,
          }}
        />
      ) : (
        <Stack.Screen
          options={{
            title: 'Rental Extension',
            headerTitle: 'Rental Extension',
            headerLeft: () => <BackButton />,
            headerShown: true,
            headerStyle: {
              backgroundColor: theme.colors.background_1,
            },
            headerTintColor: theme.colors.text.primary,
            headerShadowVisible: false,
          }}
        />
      )}

      {Platform.OS === 'web' && isHandlingPaymentPending ? (
        <View
          style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1,
            backgroundColor: theme.colors.background_1,
          }}
        >
          <Loader style={{ width: 150, height: 150 }} />
        </View>
      ) : null}

      <View
        style={{
          padding: 16,
        }}
      >
        {['md', 'lg'].includes(UnistylesRuntime.breakpoint) ? (
          <View style={{ gap: 16 }}>
            <NavigationButton
              type={NavigationButtonTypes.back_with_text}
              onPress={() => {
                router.back();
              }}
            />

            <Text type="largeTitle">Rental Extension</Text>
          </View>
        ) : null}

        <ScrollView
          showsVerticalScrollIndicator={false}
          contentContainerStyle={styles.scrollContent}
        >
          <View style={{ flexGrow: 1, gap: 32 }}>
            <View style={{ gap: 24 }}>
              <View>
                <Text type="title1">Choose Extension</Text>
              </View>

              <View style={{ gap: 8 }}>
                <Text
                  type="caption1"
                  style={{ color: theme.colors.text.secondary }}
                >
                  Quick options
                </Text>

                <View
                  style={{
                    gap: 8,
                    flexDirection: 'row',
                    flexWrap: 'wrap',
                    justifyContent: 'space-between',
                  }}
                >
                  {availableExtensionsData?.quickExtensionOptions.map(
                    (option) => {
                      return (
                        <CardItem
                          key={option.name}
                          style={{
                            width:
                              Platform.OS === 'web' ? 'calc(50% - 4px)' : '48%',

                            height: 70,
                          }}
                          onPress={() => {
                            if (option.available) {
                              if (
                                activeQuickExtensionOption?.name === option.name
                              ) {
                                setActiveQuickExtensionOption(null);
                              } else {
                                setActiveQuickExtensionOption(option);
                                handleCreateExtensionIntent(
                                  format(
                                    toZonedTime(
                                      option.newEndTime,
                                      resData.pickupLocation.timezone
                                    ),
                                    'yyyy-MM-dd HH:mm:ss'
                                  )
                                );
                              }
                            }
                          }}
                          active={
                            activeQuickExtensionOption?.name === option.name
                          }
                          disabled={!option.available}
                          title={option.name}
                          body={
                            <View style={{ flexDirection: 'row', gap: 4 }}>
                              {option.available ? (
                                <>
                                  <Text
                                    type="caption1"
                                    style={{
                                      color: theme.colors.text.secondary,
                                      textDecorationLine:
                                        option.discountedDailyRate > 0
                                          ? 'line-through'
                                          : 'none',
                                    }}
                                  >
                                    ${option.dailyRate}/day
                                  </Text>

                                  {option.discountedDailyRate ? (
                                    <Text
                                      type="caption1"
                                      style={{
                                        color: theme.colors.chip.brand.color,
                                      }}
                                    >
                                      ${option.discountedDailyRate}/day
                                    </Text>
                                  ) : null}
                                </>
                              ) : (
                                <Text
                                  type="caption1"
                                  style={{
                                    color: theme.colors.text.secondary50,
                                  }}
                                >
                                  Not available
                                </Text>
                              )}
                            </View>
                          }
                        />
                      );
                    }
                  )}
                </View>
              </View>

              <View style={{ gap: 8 }}>
                <Text
                  type="caption1"
                  style={{ color: theme.colors.text.secondary }}
                >
                  Or select a new end date & time
                </Text>

                <Pressable
                  onPress={() => {
                    setShowCalendar(true);
                  }}
                  style={[
                    styles.calendarButton,
                    ...(extensionIntent && !activeQuickExtensionOption
                      ? [{ borderColor: theme.colors.chip.brand.color }]
                      : []),
                    ...(extensionUnavailable
                      ? [
                          {
                            borderColor: theme.colors.card.disabled.borderColor,
                            backgroundColor:
                              theme.colors.card.disabled.backgroundColor,
                          },
                        ]
                      : []),
                  ]}
                  disabled={extensionUnavailable}
                >
                  <Icon
                    type="naked"
                    icon={<CalendarIcon />}
                    fillColor={
                      extensionUnavailable
                        ? theme.colors.text.secondary50
                        : theme.colors.icon.color
                    }
                    width={22}
                    height={22}
                  />

                  {endDatetime && !extensionUnavailable ? (
                    <View>
                      <Text
                        type="bodyRegular"
                        style={{
                          fontSize: 14,
                        }}
                      >
                        {format(
                          toZonedTime(
                            endDatetime,
                            resData.pickupLocation.timezone
                          ),
                          'MMM dd yyyy | hh:mm a'
                        )}
                      </Text>
                    </View>
                  ) : (
                    <Text
                      type="bodyRegular"
                      style={{ color: theme.colors.text.secondary50 }}
                    >
                      Not available
                    </Text>
                  )}
                </Pressable>
              </View>

              <View style={{ flexDirection: 'row', gap: 8 }}>
                <Text
                  type="caption1"
                  style={{ color: theme.colors.text.secondary }}
                >
                  Extension not available? - Contact support
                </Text>

                <Tooltip text="Sometimes, the car may not be available for certain dates. If you’d like to extend your trip on dates that are currently unavailable, please reach out to customer support." />
              </View>
            </View>

            <View
              style={{
                height: 1,
                width: '100%',
                backgroundColor: theme.colors.card.default.borderColor,
              }}
            />

            {isCreatingExtensionIntent ? (
              <Loader style={{ width: '100%', height: 150 }} />
            ) : null}

            {!isCreatingExtensionIntent && extensionIntent ? (
              <Card style={{ overflow: 'hidden' }}>
                <View
                  style={{
                    padding: 16,
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Text type="bodyBold">Extension Price Summary</Text>

                  {extensionIntent.data.numHoursAdded > 0 ? (
                    <Chip
                      type={ChipType.BRAND}
                      text={`+${extensionIntent.data.numHoursAdded} ${
                        extensionIntent.data.numHoursAdded > 1
                          ? 'hours'
                          : 'hour'
                      }`}
                    />
                  ) : (
                    <Chip
                      type={ChipType.BRAND}
                      text={`+${extensionIntent.data.numDaysAdded} ${
                        extensionIntent.data.numDaysAdded > 1 ? 'days' : 'day'
                      }`}
                    />
                  )}
                </View>

                <View>
                  <View
                    style={{
                      paddingLeft: 16,
                      paddingRight: 16,
                      paddingTop: 8,
                      paddingBottom: 8,
                      backgroundColor: theme.colors.card.hover.backgroundColor,
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Text type="bodyRegular">
                      {format(
                        toZonedTime(
                          resData.endTime,
                          resData.pickupLocation.timezone
                        ),
                        'MMM dd - hh:mm a'
                      )}
                    </Text>

                    <Icon
                      type="naked"
                      icon={<ArrowRight />}
                      fillColor={theme.colors.icon.color}
                      width={10}
                      height={10}
                    />

                    <Text
                      type="bodyRegular"
                      style={{ color: theme.colors.chip.brand.color }}
                    >
                      {format(
                        toZonedTime(
                          extensionIntent.data.endTime,
                          resData.pickupLocation.timezone
                        ),
                        'MMM dd - hh:mm a'
                      )}
                    </Text>
                  </View>
                </View>

                {extensionIntent.data.numDaysAdded > 0 ? (
                  <View>
                    <View style={{ padding: 16, gap: 8 }}>
                      <View>
                        <Text type="bodyRegular">
                          {`${resData.car.brandName} ${resData.car.modelName} - ${resData.car.trim.displayName}`}
                        </Text>

                        <View
                          style={{
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <Text
                            type="bodyRegular"
                            style={{ color: theme.colors.text.secondary }}
                          >
                            ${extensionIntent.data.dailyRate}/day
                          </Text>

                          <Text
                            type="bodyRegular"
                            style={{ color: theme.colors.text.secondary }}
                          >
                            x{extensionIntent.data.numDaysAdded} days
                          </Text>

                          <Text type="bodyRegular">
                            $
                            {extensionIntent.data.numDaysAdded *
                              extensionIntent.data.dailyRate}
                          </Text>
                        </View>
                      </View>

                      {extensionIntent.data?.weeklyDiscount > 0 ? (
                        <View
                          style={{
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                          }}
                        >
                          <Text
                            type="bodyRegular"
                            style={{ color: theme.colors.chip.brand.color }}
                          >
                            Weekly Discount
                          </Text>

                          <Text
                            type="bodyRegular"
                            style={{ color: theme.colors.chip.brand.color }}
                          >
                            -${extensionIntent.data.weeklyDiscount}
                          </Text>
                        </View>
                      ) : null}

                      {extensionIntent.data.monthlyDiscount > 0 ? (
                        <View
                          style={{
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                          }}
                        >
                          <Text
                            type="bodyRegular"
                            style={{ color: theme.colors.chip.brand.color }}
                          >
                            Monthly Discount
                          </Text>

                          <Text
                            type="bodyRegular"
                            style={{ color: theme.colors.chip.brand.color }}
                          >
                            -${extensionIntent.data.monthlyDiscount}
                          </Text>
                        </View>
                      ) : null}

                      <View>
                        <Text type="bodyRegular">
                          {resData.insurance.category}
                        </Text>

                        <View
                          style={{
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <Text
                            type="bodyRegular"
                            style={{ color: theme.colors.text.secondary }}
                          >
                            $
                            {formatNumber(
                              extensionIntent.data.insuranceDailyRate
                            )}
                            /day
                          </Text>

                          <Text
                            type="bodyRegular"
                            style={{ color: theme.colors.text.secondary }}
                          >
                            x{extensionIntent.data.numDaysAdded} days
                          </Text>

                          <Text type="bodyRegular">
                            $
                            {formatNumber(
                              extensionIntent.data.numDaysAdded *
                                extensionIntent.data.insuranceDailyRate
                            )}
                          </Text>
                        </View>
                      </View>

                      {extensionIntent.data.recurringExtras?.length > 0 ? (
                        <View>
                          {extensionIntent.data.recurringExtras.map(
                            (extra: Extra) => {
                              return (
                                <View key={extra.id}>
                                  <Text type="bodyRegular">{extra.title}</Text>

                                  <View
                                    style={{
                                      flexDirection: 'row',
                                      justifyContent: 'space-between',
                                      alignItems: 'center',
                                    }}
                                  >
                                    <Text
                                      type="bodyRegular"
                                      style={{
                                        color: theme.colors.text.secondary,
                                      }}
                                    >
                                      ${extra.amount}/day
                                    </Text>

                                    <Text
                                      type="bodyRegular"
                                      style={{
                                        color: theme.colors.text.secondary,
                                      }}
                                    >
                                      x{extensionIntent.data.numDaysAdded} days
                                    </Text>

                                    <Text type="bodyRegular">
                                      $
                                      {formatNumber(
                                        extensionIntent.data.numDaysAdded *
                                          extra.amount
                                      )}
                                    </Text>
                                  </View>
                                </View>
                              );
                            }
                          )}
                        </View>
                      ) : null}
                    </View>
                  </View>
                ) : null}

                <View
                  style={{
                    height: 1,
                    width: '100%',
                    backgroundColor: theme.colors.card.default.borderColor,
                  }}
                />

                <View>
                  <View
                    style={{
                      padding: 16,
                      gap: 8,
                    }}
                  >
                    <View
                      style={{
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Text type="bodyRegular">Subtotal</Text>

                      <Text type="bodyRegular">
                        {extensionIntent.data.subtotal
                          ? `$${formatNumber(extensionIntent.data.subtotal)}`
                          : 'Free'}
                      </Text>
                    </View>

                    {extensionIntent.data.outstandingBalance > 0 ? (
                      <View
                        style={{
                          flexDirection: 'row',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Pressable
                          onPress={() => {
                            router.push(`/account/trip-history/${id}`);
                          }}
                        >
                          <Text
                            type="bodyRegular"
                            style={{ textDecorationLine: 'underline' }}
                          >
                            Outstanding balance
                          </Text>
                        </Pressable>

                        <Text type="bodyRegular">
                          $
                          {formatNumber(
                            extensionIntent.data.outstandingBalance
                          )}
                        </Text>
                      </View>
                    ) : null}
                  </View>
                </View>

                <View
                  style={{
                    width: '100%',
                    backgroundColor: theme.colors.card.hover.backgroundColor,
                    padding: 16,
                  }}
                >
                  <View
                    style={{
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                    }}
                  >
                    <View
                      style={{
                        flexDirection: 'row',
                        gap: 8,
                        alignItems: 'center',
                      }}
                    >
                      <Text type="bodyBold">Amount Due Now</Text>

                      {extensionIntent.data.numDaysAdded > 0 ? (
                        <Tooltip text="Your outstanding balance needs to be settled before you can extend" />
                      ) : null}
                    </View>

                    <Text type="bodyBold">
                      {extensionIntent.data.numHoursAdded > 0
                        ? 'Free'
                        : `$${formatNumber(
                            extensionIntent.data.subtotal +
                              extensionIntent.data.outstandingBalance
                          )}`}
                    </Text>
                  </View>
                </View>
              </Card>
            ) : null}

            {extensionIntent?.data.numHoursAdded > 0 ? (
              <Button
                text="Confirm & Extend"
                type="primary"
                onPress={() => {
                  handleFreeExtension();
                }}
              />
            ) : null}

            {extensionIntent && extensionIntent.data.stripeCustomerId ? (
              <ExtensionPayment
                reservation={resData}
                extensionIntent={extensionIntent}
                onPaymentInitiated={() => {
                  setIsHandlingPaymentPending(true);
                  setIsHandlingPaymentError(false);
                }}
                onPaymentSucceeded={() => {
                  setIsHandlingPaymentPending(false);
                  setIsHandlingPaymentSuccess(true);

                  handlePaymentSuccessful();
                }}
                onPaymentFailed={() => {
                  setIsHandlingPaymentPending(false);
                  setIsHandlingPaymentError(true);
                }}
              />
            ) : null}

            <ContactSupport />
          </View>
        </ScrollView>
      </View>

      <Calendar
        isVisible={showCalendar}
        onHide={() => {
          setShowCalendar(false);
        }}
        minDate={addHours(
          toZonedTime(resData.endTime, resData.pickupLocation.timezone),
          1
        )}
        maxDate={maxDate}
        defaultStartDate={
          (endDatetime &&
            toZonedTime(endDatetime, resData.pickupLocation.timezone)) ||
          endDatetime
        }
        onDatesSelected={(endDt) => {
          const dt = DateTime.fromObject({
            year: endDt.getFullYear(),
            month: endDt.getMonth() + 1, // Month is 0-indexed in JS Date, 1-indexed in Luxon
            day: endDt.getDate(),
            hour: endDt.getHours(),
            minute: endDt.getMinutes(),
          }).setZone(resData.pickupLocation.timezone, { keepLocalTime: true });

          const localTime = dt.setZone('local');

          setEndDatetime(localTime.toJSDate());
          handleCreateExtensionIntent(dt.toFormat('yyyy-MM-dd HH:mm:ss'));
          setShowCalendar(false);
          setActiveQuickExtensionOption(null);
        }}
        onDateSelected={(endDt) => {}}
        onTimeSelected={(endDt) => {
          const yo = DateTime.fromObject({
            year: endDt.getFullYear(),
            month: endDt.getMonth() + 1, // Month is 0-indexed in JS Date, 1-indexed in Luxon
            day: endDt.getDate(),
            hour: endDt.getHours(),
            minute: endDt.getMinutes(),
          })
            .setZone(resData.pickupLocation.timezone, { keepLocalTime: true })
            .setZone('local');

          console.log('time selected', endDt, endDatetime, yo.toJSDate());
          if (isAfter(endDt, endDatetime!)) {
          } else {
            setEndDatetime(yo.toJSDate());
          }
        }}
        range={false}
        mode="datetime"
        id="trip-extension-calendar"
        containerStyle={{
          height: '90%',
        }}
        showDesktopConfirm
        headerTitle="Date & Time"
        hideAfterTimeSelection={false}
      />
    </ScrollView>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background_1,
  },
  scrollViewContainer: {
    paddingBottom: 0,
    height: {
      xs: '88%',
      md: '100%',
    },
    width: '100%',
  },
  scrollViewChild: {
    width: {
      xs: '100%',
      md: 600,
    },
    gap: 16,
    paddingBottom: 16,
  },
  scrollContent: {
    width: {
      xs: '100%',
      md: 600,
    },
    gap: 16,
    paddingBottom: 16,
  },
  calendarButton: {
    height: 56,
    borderColor: theme.colors.input.default.border,
    borderWidth: 1,
    borderRadius: 8,
    alignItems: 'center',
    paddingLeft: 16,
    flexDirection: 'row',
    gap: 16,
    backgroundColor: theme.colors.background_2,
  },
  overviewContainer: {
    flexGrow: 1,
    backgroundColor: theme.colors.background_2,
    display: {
      md: 'none',
    },
  },
}));
